import React from "react";
import {
  Badge,
  Form,
  ListGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { CheckCircle, Clock, Smartphone } from "react-feather";
import {
  IQaReviewListItem,
  FULL_AUTO_COUPONS_TARGETING_FLAG,
} from "../../interfaces";
import { format, differenceInDays } from "date-fns";

interface ReviewItemListProps {
  items: IQaReviewListItem[];
  selectedItemId: string | null;
  onSelectItem: (item: IQaReviewListItem) => void;
  searchFilter: string;
  countryFilter: string;
  untestedOnly: boolean;
  notTestedDays: string;
  onSearchChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onUntestedChange: (value: boolean) => void;
  onNotTestedDaysChange: (value: string) => void;
  countries: string[];
  onToggleFlag?: (item: IQaReviewListItem, flag: string) => void;
  fullAutoFlagFilter?: "" | "flagged" | "unflagged";
  onFullAutoFlagFilterChange?: (value: "" | "flagged" | "unflagged") => void;
}

export const ReviewItemList: React.FC<ReviewItemListProps> = ({
  items,
  selectedItemId,
  onSelectItem,
  searchFilter,
  countryFilter,
  untestedOnly,
  notTestedDays,
  onSearchChange,
  onCountryChange,
  onUntestedChange,
  onNotTestedDaysChange,
  countries,
  onToggleFlag,
  fullAutoFlagFilter = "",
  onFullAutoFlagFilterChange,
}) => {
  const now = new Date();

  const filteredItems = items.filter((item) => {
    if (
      searchFilter &&
      !item.domain.toLowerCase().includes(searchFilter.toLowerCase())
    ) {
      return false;
    }
    if (countryFilter && item.countryCode !== countryFilter) {
      return false;
    }
    if (untestedOnly && item.lastTestedAt) {
      return false;
    }
    if (fullAutoFlagFilter === "flagged") {
      if (!item.flags || !item.flags.includes(FULL_AUTO_COUPONS_TARGETING_FLAG))
        return false;
    } else if (fullAutoFlagFilter === "unflagged") {
      if (item.flags && item.flags.includes(FULL_AUTO_COUPONS_TARGETING_FLAG))
        return false;
    }
    if (notTestedDays) {
      const days = Number(notTestedDays);
      if (days > 0) {
        if (item.lastTestedAt) {
          const daysSince = differenceInDays(now, new Date(item.lastTestedAt));
          if (daysSince < days) return false;
        }
      }
    }
    return true;
  });

  // Sort: untested first, then by last tested ascending
  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!a.lastTestedAt && b.lastTestedAt) return -1;
    if (a.lastTestedAt && !b.lastTestedAt) return 1;
    if (a.lastTestedAt && b.lastTestedAt) {
      return (
        new Date(a.lastTestedAt).getTime() - new Date(b.lastTestedAt).getTime()
      );
    }
    return 0;
  });

  return (
    <div className="d-flex flex-column h-100">
      {/* Filters */}
      <div className="p-2 border-bottom">
        <Form.Control
          size="sm"
          placeholder="Search domain..."
          value={searchFilter}
          onChange={(e) => onSearchChange(e.target.value)}
          className="mb-1"
        />
        <div className="d-flex gap-1 mb-1">
          <Form.Select
            size="sm"
            value={countryFilter}
            onChange={(e) => onCountryChange(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
          <Form.Control
            size="sm"
            type="number"
            placeholder="Days"
            title="Not tested in N days"
            value={notTestedDays}
            onChange={(e) => onNotTestedDaysChange(e.target.value)}
            style={{ width: 70 }}
          />
        </div>
        <Form.Check
          type="switch"
          label="Untested only"
          checked={untestedOnly}
          onChange={(e) => onUntestedChange(e.target.checked)}
          className="small"
        />
        {onFullAutoFlagFilterChange && (
          <Form.Select
            size="sm"
            className="mt-1"
            value={fullAutoFlagFilter}
            onChange={(e) =>
              onFullAutoFlagFilterChange(
                e.target.value as "" | "flagged" | "unflagged"
              )
            }
          >
            <option value="">Full Auto: any</option>
            <option value="flagged">Full Auto: flagged</option>
            <option value="unflagged">Full Auto: not flagged</option>
          </Form.Select>
        )}
        <div className="text-muted small mt-1">
          {sortedItems.length} of {items.length} items
        </div>
      </div>

      {/* Item list */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <ListGroup variant="flush">
          {sortedItems.map((item) => {
            const isSelected = item._id === selectedItemId;
            const isTested = !!item.lastTestedAt;
            const isFullAuto =
              !!item.flags &&
              item.flags.includes(FULL_AUTO_COUPONS_TARGETING_FLAG);
            return (
              <ListGroup.Item
                key={item._id}
                active={isSelected}
                action
                onClick={() => onSelectItem(item)}
                className="py-2 px-2"
                style={{ cursor: "pointer" }}
              >
                <div>
                  <div
                    className="fw-semibold small text-truncate"
                    title={item.domain}
                  >
                    {item.domain}
                  </div>
                  <div className="d-flex align-items-center gap-1 mt-1">
                    <Badge
                      bg={isSelected ? "light" : "secondary"}
                      text={isSelected ? "dark" : undefined}
                      className="small"
                    >
                      {item.countryCode}
                    </Badge>
                    {isTested ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Tested{" "}
                            {format(
                              new Date(item.lastTestedAt!),
                              "yyyy-MM-dd HH:mm"
                            )}
                            {item.testedByName && ` by ${item.testedByName}`}
                          </Tooltip>
                        }
                      >
                        <span>
                          <CheckCircle size={12} className="text-success" />
                        </span>
                      </OverlayTrigger>
                    ) : (
                      <Clock size={12} className="text-warning" />
                    )}
                    {onToggleFlag && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {isFullAuto
                              ? "Remove Full Auto Coupons Targeting flag"
                              : "Flag for Full Auto Coupons Targeting"}
                          </Tooltip>
                        }
                      >
                        <span
                          role="button"
                          className="ms-auto d-inline-flex align-items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleFlag(
                              item,
                              FULL_AUTO_COUPONS_TARGETING_FLAG
                            );
                          }}
                        >
                          <Smartphone
                            size={18}
                            className={
                              isFullAuto ? "text-primary" : "text-muted"
                            }
                          />
                        </span>
                      </OverlayTrigger>
                    )}
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
          {sortedItems.length === 0 && (
            <div className="text-muted text-center py-3 small">
              No items match filters
            </div>
          )}
        </ListGroup>
      </div>
    </div>
  );
};
